// ═══════════════════════════════════════════════════════════════════
// PORTFOLIO DATA - Niko Knez
// Echte Daten aus dem Portfolio Briefing
// ═══════════════════════════════════════════════════════════════════

import { LucideIcon, Palette, Server, Terminal, Sparkles } from 'lucide-react';

export interface NikoProject {
  id: string;
  title: string;
  company: string;
  role: string;
  period: string;
  description: string;
  tech: string[];
  url?: string;
  industry?: string;
  current?: boolean;
}

export interface ISkillCategory {
  title: string;
  icon: LucideIcon;
  skills: Array<{ name: string; level: 'expert' | 'advanced' | 'intermediate' }>;
}

export interface IExperience {
  id: string;
  company: string;
  role: string;
  period: string;
  description: string;
  highlights: string[];
  current: boolean;
}

export interface IIndustry {
  id: string;
  name: string;
  project: string;
}

export interface INikoInfo {
  name: string;
  title: string;
  tagline: string;
  location: string;
  email: string;
  experience: string;
  highlights: string[];
  socials: Array<{ name: string; url: string }>;
  shortBio: string;
  fullBio: string;
  website: string;
}

// ═══════════════════════════════════════════════════════════════════
// Personal Info
// ═══════════════════════════════════════════════════════════════════

export const nikoInfo: INikoInfo = {
  name: 'Niko Knez',
  title: 'Senior Fullstack Developer & UX/UI Designer',
  tagline: 'Digitale Produkte, die begeistern',
  location: 'Köln, Deutschland',
  email: 'info@design-nk.de',
  experience: '12+',
  highlights: [
    '50+ Projekte',
    'DAX-Konzerne',
    'KI-Integration',
    'Team Lead',
    'Eigene Agentur',
  ],
  socials: [
    { name: 'LinkedIn', url: 'https://linkedin.com/in/niko-knez' },
    { name: 'GitHub', url: 'https://github.com/nikoknez' },
    { name: 'Email', url: 'mailto:info@design-nk.de' },
  ],
  shortBio: 'Digitale Produkte, die begeistern.',
  fullBio:
    'Senior Fullstack Developer und UX/UI Designer mit über 12 Jahren Erfahrung in der Konzeption, Gestaltung und technischen Umsetzung digitaler Produkte. Starker Fokus auf nutzerzentrierte Produktentwicklung, skalierbare Webarchitekturen und nachhaltige Codequalität.',
  website: 'design-nk.de',
};

// ═══════════════════════════════════════════════════════════════════
// Industries
// ═══════════════════════════════════════════════════════════════════

export const industries: IIndustry[] = [
  { id: 'automotive', name: 'Automotive', project: 'Mercedes-Benz AG' },
  { id: 'energy', name: 'Energie', project: 'E.ON, CEETRAC' },
  { id: 'pharma', name: 'Pharma', project: 'Procter & Gamble' },
  { id: 'software', name: 'Software & KI', project: 'Systempiloten, Valsight' },
  { id: 'fintech', name: 'FinTech & Insurance', project: 'KPM-PB, Smart-Pension' },
  { id: 'ecommerce', name: 'E-Commerce', project: 'Best Price, Positec' },
  { id: 'healthcare', name: 'Healthcare', project: 'NeuroLab, NeuroVital' },
  { id: 'security', name: 'IT-Security', project: 'Datasec' },
];

// ═══════════════════════════════════════════════════════════════════
// Skills
// ═══════════════════════════════════════════════════════════════════

export const skillCategories: ISkillCategory[] = [
  {
    title: 'Frontend',
    icon: Palette,
    skills: [
      { name: 'React', level: 'expert' },
      { name: 'Next.js', level: 'expert' },
      { name: 'Vue.js', level: 'expert' },
      { name: 'Nuxt.js', level: 'advanced' },
      { name: 'TypeScript', level: 'expert' },
      { name: 'JavaScript (ES6+)', level: 'expert' },
      { name: 'Tailwind CSS', level: 'expert' },
      { name: 'SCSS/SASS', level: 'advanced' },
      { name: 'HTML5/CSS3', level: 'expert' },
    ],
  },
  {
    title: 'Backend',
    icon: Server,
    skills: [
      { name: 'Node.js', level: 'advanced' },
      { name: 'PHP', level: 'expert' },
      { name: 'MySQL', level: 'advanced' },
      { name: 'REST APIs', level: 'expert' },
      { name: 'WordPress', level: 'expert' },
    ],
  },
  {
    title: 'Tools & DevOps',
    icon: Terminal,
    skills: [
      { name: 'Git / GitHub', level: 'expert' },
      { name: 'Docker', level: 'intermediate' },
      { name: 'VS Code', level: 'expert' },
      { name: 'Jira / Confluence', level: 'advanced' },
      { name: 'Agile / Scrum', level: 'advanced' },
    ],
  },
  {
    title: 'Design',
    icon: Sparkles,
    skills: [
      { name: 'Figma', level: 'expert' },
      { name: 'Adobe XD', level: 'advanced' },
      { name: 'Photoshop', level: 'advanced' },
      { name: 'Illustrator', level: 'intermediate' },
      { name: 'Design Systems', level: 'expert' },
      { name: 'Prototyping', level: 'advanced' },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════
// Experience (Aktuelle + Wichtigste)
// ═══════════════════════════════════════════════════════════════════

export const experiences: IExperience[] = [
  {
    id: 'systempiloten',
    company: 'Systempiloten GmbH',
    role: 'Senior Fullstack Entwickler & UX Architekt',
    period: '11/2023 – Heute',
    description:
      'Entwicklung einer modularen Plattform für ein KI-gestütztes CMS/CRM mit integriertem Webbuilder. Architekturentscheidungen, Frontend- und Backend-Entwicklung, UX-Konzeption.',
    highlights: [
      'KI-gestützter Webbuilder Entwicklung',
      'Architekturentscheidungen & Code-Reviews',
      'Technisches Mentoring',
    ],
    current: true,
  },
  {
    id: 'nk-design',
    company: 'NK Design',
    role: 'Geschäftsführer, Lead Developer & UX/UI Designer',
    period: '03/2018 – Heute',
    description:
      'Aufbau und Führung einer eigenen Agentur für digitale Produkte. Full-Service von Strategie, UX-Konzeption bis zur technischen Umsetzung.',
    highlights: [
      '50+ erfolgreiche Projekte',
      'Full-Service Agentur',
      'DAX-Konzern Kunden',
    ],
    current: true,
  },
  {
    id: 'scholz-friends',
    company: 'Scholz & Friends',
    role: 'Full Stack Developer & UX Partner',
    period: '10/2021 – Heute',
    description:
      'Entwicklung komplexer WordPress-Plattformen für die Apothekenbranche (Procter & Gamble). Vue.js Frontend-Komponenten und Performance-Optimierung.',
    highlights: [
      'Procter & Gamble Projekt',
      'Vue.js & WordPress',
      'Performance-Optimierung',
    ],
    current: true,
  },
  {
    id: 'mercedes',
    company: 'Mercedes-Benz AG',
    role: 'Technical Project Manager E-Commerce',
    period: '11/2022 – 04/2023',
    description:
      'Steuerung und technischer Rollout eines neuen internationalen Online-Shops. API-Schnittstellenmanagement und Koordination internationaler Teams.',
    highlights: [
      'Online-Shop Rollout in 5 Märkte',
      'API & SAP Commerce',
      'Internationale Teams',
    ],
    current: false,
  },
  {
    id: 'positec',
    company: 'Positec Germany GmbH',
    role: 'Fullstack Developer, UX/UI Designer & Tech Lead',
    period: '10/2021 – 02/2023',
    description:
      'Entwicklung und technische Leitung von acht internationalen Unternehmenswebsites sowie einer mobilen App.',
    highlights: [
      '8 internationale Websites',
      'Mobile App (Kotlin/Swift)',
      'Tech Lead Position',
    ],
    current: false,
  },
];

// ═══════════════════════════════════════════════════════════════════
// Projects (Featured)
// ═══════════════════════════════════════════════════════════════════

export const featuredProjects: NikoProject[] = [
  {
    id: 'neowispr',
    title: 'Native AI Dictation für macOS',
    company: 'NeoWispr',
    role: 'Founder, Product Engineer & UX Lead',
    period: '2026 – Heute',
    description:
      'Entwicklung einer nativen macOS-Diktierapp mit Offline-STT, Live-Übersetzung, optionalem Cleanup und eigener Begriffspflege.',
    tech: ['Swift', 'Whisper', 'macOS', 'TypeScript', 'AI Agents'],
    industry: 'Neon Product Lab',
    current: true,
  },
  {
    id: 'neovoice',
    title: 'Voice Layer für lokale AI-Flows',
    company: 'NeoVoice',
    role: 'Founder, Voice UX & Runtime Engineering',
    period: '2026 – Heute',
    description:
      'Voice-Assistant-Schicht für schnelle Gespräche, lokale Audio-Flows, Interruptions und eine natürlichere Steuerung von AI-Agenten.',
    tech: ['Swift', 'Audio Routing', 'Realtime', 'macOS', 'AI Agents'],
    industry: 'Neon Product Lab',
    current: true,
  },
  {
    id: 'neon-core',
    title: 'Agent OS mit Gateway & Memory',
    company: 'Neon Core',
    role: 'System Architect & Product Engineer',
    period: '2026 – Heute',
    description:
      'Neon-native Agent-Runtime mit Gateway, Memory-Kontext, Skills, Mission Control und echten geprüften Agent-Runs.',
    tech: ['TypeScript', 'Node.js', 'Discord Gateway', 'Memory', 'Mission Control'],
    industry: 'Neon Product Lab',
    current: true,
  },
  {
    id: 'systempiloten',
    title: 'KI-gestütztes CMS/CRM System',
    company: 'Systempiloten GmbH',
    role: 'Senior Fullstack Entwickler & UX Architekt',
    period: '11/2023 – Heute',
    description:
      'Entwicklung einer modularen Plattform für ein KI-gestütztes CMS/CRM mit integriertem Webbuilder. Architekturentscheidungen, Frontend- und Backend-Entwicklung, UX-Konzeption.',
    tech: ['React', 'Vue.js', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Node.js', 'KI-Integration'],
    industry: 'Software & KI',
    current: true,
  },
  {
    id: 'ceetrac',
    title: 'Nachhaltigkeits-Management Plattform',
    company: 'CEETRAC / CEEMAS',
    role: 'Lead Frontend Developer / UX/UI Designer',
    period: '11/2024 – 03/2025',
    description:
      'Komplettes Redesign und Frontend-Neuentwicklung der Nachhaltigkeits-Management-Plattform. Vue.js-Applikation mit Dashboard, Datenvisualisierung und Multi-User-System.',
    tech: ['Vue.js', 'TypeScript', 'Tailwind CSS', 'REST APIs', 'Dashboard'],
    url: 'ceetrac.de',
    industry: 'Energie',
  },
  {
    id: 'datasec',
    title: 'Datensicherheits-Plattform',
    company: 'Datasec',
    role: 'React Frontend Developer',
    period: '11/2025 – 12/2025',
    description:
      'Entwicklung des kompletten Frontends für eine Datensicherheits-Plattform. Moderne, performante Single-Page-Application mit React und TypeScript.',
    tech: ['React', 'TypeScript', 'REST APIs', 'Modern CSS'],
    url: 'p-f7pgr7.project.space',
    industry: 'IT-Security',
  },
  {
    id: 'levaco',
    title: 'Corporate Website Redesign',
    company: 'Levaco Chemicals',
    role: 'Fullstack Developer / Lead UX/UI Designer',
    period: '10/2025 – 01/2026',
    description:
      'Komplettes Redesign und Neuentwicklung der Unternehmenswebsite. UX/UI Konzeption, WordPress-Entwicklung mit Elementor, Performance-Optimierung und SEO.',
    tech: ['WordPress', 'Elementor Pro', 'PHP', 'JavaScript', 'CSS'],
    url: 'levaco.wpcomstaging.com',
    industry: 'Chemie',
  },
  {
    id: 'mercedes',
    title: 'E-Commerce Shop Rollout',
    company: 'Mercedes-Benz AG',
    role: 'Technical Project Manager E-Commerce',
    period: '11/2022 – 04/2023',
    description:
      'Technische Leitung des Online-Shop Rollouts für Mercedes-Benz. API-Schnittstellenmanagement und Koordination internationaler Entwicklungsteams.',
    tech: ['REST APIs', 'SAP Commerce', 'Jira', 'Confluence', 'Agile/Scrum'],
    industry: 'Automotive',
  },
  {
    id: 'eon',
    title: 'Kunden-Portal Optimierung',
    company: 'E.ON Energie Deutschland GmbH',
    role: 'UX Designer',
    period: '10/2021 – 01/2022',
    description:
      'Konzeption und Optimierung von Userflows im Energieumfeld. Überarbeitung von Header-Strukturen, Entwicklung neuer Solar-Features und Optimierung digitaler Produktberater.',
    tech: ['Figma', 'Sketch', 'Jira', 'Zeplin'],
    industry: 'Energie',
  },
  {
    id: 'valsight',
    title: 'KI Business Intelligence UI',
    company: 'Valsight GmbH',
    role: 'UX/UI Designer',
    period: '09/2021 – 02/2022',
    description:
      'Gestaltung und Konzeption des UX/UI-Designs einer KI-basierten Business-Intelligence-Software zur Analyse von Umsatztrends und Risikoerkennung.',
    tech: ['Figma', 'Adobe XD', 'HTML', 'CSS', 'JavaScript'],
    industry: 'Software & KI',
  },
];

export const stats = [
  { label: 'Jahre Erfahrung', value: '12+' },
  { label: 'Projekte', value: '50+' },
  { label: 'DAX-Unternehmen', value: '2' },
  { label: 'Technologien', value: '25+' },
];
